<script setup lang="ts">
import CardContainer from '@/components/Card/card-container.vue'
import type { invoiceSchema } from '@/features/invoices/schemas'
import type { z } from 'zod'
import { ChargeType } from '@/api/billing/constants'
import CardHeader from '@/components/Card/card-header.vue'
import InputToggleGroup from '@/components/input-toggle-group.vue'
import IconHelp from '@/shared/components/IconHelp.vue'
import CardContent from '@/components/Card/card-content.vue'
import { ref, reactive, computed } from 'vue'
import InputSelect from '@/components/input-select.vue'
import InputCheckbox from '@/components/input-checkbox.vue'
import InputNumber from '@/components/input-number.vue'
import { formatNumber, formatToFractionalCurrency, formatToMainCurrency } from '@/helpers/formatting'
import useNotifications from '@/composables/notifications'
import useLoader from '@/composables/loader'
import { useDeleteInvoiceCharge } from '@/composables/billing/invoices'
import ModalVariantConfirmation from '@/components/modal/modal-variant-confirmation.vue'
import DropdownContext from '@/components/dropdown-context.vue'
import DropdownItem from '@/components/dropdown-item.vue'
import { chargeTypeDisplayNames, Seasons, TimeOfUsePeriods } from '../../constants'
import { calculateInvoiceCategoryTotalInMainCurrency } from '@/features/invoices/lib/invoice-charge-totals'

type Category = z.infer<typeof invoiceSchema>['categories'][number]
type Charge = Category['charges'][number]
type TrackedChange<TItem> = { id: number, values: Partial<TItem>, categoryId?: number }
const tracked = reactive<{ charges: TrackedChange<Charge>[], categories: TrackedChange<Category>[] }>({ charges: [], categories: [] })

const props = withDefaults(
    defineProps<{
        invoiceId: number
        assetCode: string
        categories: Category[]
        editableChargeTypes?: ChargeType[]
        removableChargeTypes?: ChargeType[]
        capabilities?: ('add-charge')[]
    }>(),
    {
        capabilities: () => [],
        editableChargeTypes: () => [],
        removableChargeTypes: () => []
    }
)

const { wrap } = useLoader()
const { addSuccess, addError } = useNotifications()
const { mutateAsync : deleteInvoiceCharge } = useDeleteInvoiceCharge(() => props.invoiceId)

let tempChargeId = -1

const confirmChargeCategoryDeletionId = ref<number>()
const confirmChargeDeletion = ref<Charge>()

const chargesWithChanges = computed(
  () => props.categories.reduce(
    (reduced, category) => {
      reduced[category.id] = category.charges.map(
        charge => ({
          ...charge,
          ...(tracked.charges.find(x => x.id === charge.id) ?? { values: { } }).values
        })
      )

      return reduced
    },
    { } as Record<number, Charge[]>
  )
)

function trackChange<TItem>(collection: keyof typeof tracked, id: number, newValues: Partial<TItem>, parentDetails?: {categoryId?: number}) {
  const copied = [...tracked[collection]]
  const modifiedIndex = copied.findIndex(x => x.id === id)

  if (modifiedIndex > -1) {
    copied[modifiedIndex].values = { ...copied[modifiedIndex].values, ...newValues }

    if(parentDetails?.categoryId !== undefined){
      copied[modifiedIndex].categoryId = parentDetails.categoryId
    }

  } else {
    copied.push({ id, values: newValues, categoryId: parentDetails?.categoryId })
  }

  tracked[collection] = copied
}

function addNewCharge(categoryId: number){

  const category = props.categories.filter(x => x.id == categoryId)[0]

  if(!category)
    return

  const newId = tempChargeId--

  const newCharge: Charge = {
    id:newId,
    chargeType: category.name == 'Firm' ? ChargeType.WheelingUsageFirm : ChargeType.WheelingUsageNonFirm,
    description: `Wheeling Charge`,
    demandTypeId: 0,
    seasonId: 0,
    quantity: 0,
    rateInCents: 0,
    tariffTypeId: 0,
    isQuantityModified: false,
    isRateModified: false,
    hidden: false
  }

  category.charges.push(newCharge)

  trackChange('charges', newId, newCharge, { categoryId })
}

const categoryTotalsWithChangesByCategoryId = computed(
  () => {
    const x: Record<number, number> = { }

    for (const categoryId in chargesWithChanges.value) {
      x[categoryId] = calculateInvoiceCategoryTotalInMainCurrency(chargesWithChanges.value[categoryId])
    }

    return x
  },
  { } as Record<number, number>
)

defineExpose({
  changes() {
    return {
      categories: [...tracked.categories],
      charges: [...tracked.charges],
    }
  },
  clearChanges() {
    tracked.categories = []
    tracked.charges = []
  }
})

function showInvoiceChargeDeletionModal(categoryId: number, charge: Charge){

  const chargeId = charge.id

  if (chargeId <= 0) {
    const category = props.categories.find(x => x.id === categoryId)

    if(category)
      category.charges = category.charges.filter(x => x.id !== chargeId)

    return
  }
  confirmChargeCategoryDeletionId.value = categoryId
  confirmChargeDeletion.value = charge
}

function hideInvoiceChargeDeletionModal(){
  confirmChargeCategoryDeletionId.value = undefined
  confirmChargeDeletion.value = undefined
}

function handleChargeDeletion( charge: Charge) {

  const chargeId = charge.id

  var numberOfCharges = props.categories.flatMap(x => x.charges).length

  if(numberOfCharges == 1){
    addError({
      title: 'Charge cannot be removed',
      allowHtml: true,
      message: `Charge <b>${charge.description}</b> could not be removed.   There needs to be more than one charge to allow deletion.`
    })

    hideInvoiceChargeDeletionModal()

    return
  }

  wrap(
    async () => await deleteInvoiceCharge(chargeId)
  ).then(
    () => {
      addSuccess({
        title: 'Charge removed.',
        allowHtml: true,
        message: `Charge <b>${charge.description}</b> has been removed successfully.`
      })
    },
    (e) => addError({
      title: 'Charge not removed.',
      allowHtml: true,
      message: `Charge <b>${charge.description}</b> could not be successfully removed. Please reload the page and try again.`
    })
  )

  hideInvoiceChargeDeletionModal()
}

const seasonMap: Record<number, string> = Object.fromEntries(
  Seasons.map(season => [season.id,season.name])
)

const timeOfUsePeriodsMap: Record<number, string> = Object.fromEntries(
  TimeOfUsePeriods.map(timeOfUsePeriod => [timeOfUsePeriod.id,timeOfUsePeriod.name])
)

const typeMap: Record<string, string> = {
    [ChargeType.WheelingUsageFirm]: 'WheelingUsageFirm',
    [ChargeType.WheelingUsageNonFirm]: 'WheelingUsageNonFirm'
}

function buildChargeDescription(charge: Charge) {

  const season = seasonMap[charge.seasonId] ?? ''
  const timeOfUsePeriod = charge.demandTypeId ? (timeOfUsePeriodsMap[charge.demandTypeId!] ?? '') : ''
  const chargeType = chargeTypeDisplayNames[charge.chargeType] ?? ''

  return `${timeOfUsePeriod} | ${season} | ${chargeType}`.trim()
}

const firmChargeTypes: ChargeType[] = [
  ChargeType.WheelingUsageFirm,
  ChargeType.WheelingGeneration,
  ChargeType.WheelingPenaltyDeemedEnergy,
]

const nonFirmChargeTypes: ChargeType[] = [
  ChargeType.WheelingUsageNonFirm,
  ChargeType.WheelingUsageCapped
]

const mapToChargeTypeOptions = (chargeTypes: ChargeType[]) => chargeTypes.map(chargeType => ({
  id: chargeType,
  name: chargeTypeDisplayNames[chargeType]
}))

const usedSeasonsAndTimeOfUsePeriodsPerCategory = computed(() => {

  const result: Record<number, Set<string>> = {}

  for(const category of props.categories){
    result[category.id] = new Set(
      chargesWithChanges.value[category.id].map(charge => `${charge.demandTypeId}-${charge.seasonId}`)
    )
  }

  return result
})

function getAvailableSeasons(categoryId: number, charge: Charge) {
  const usedCombinations = usedSeasonsAndTimeOfUsePeriodsPerCategory.value[categoryId]

  return Seasons.filter(season => {

    const key = `${charge.demandTypeId}-${season.id}`
    const currentChargeKey = `${charge.demandTypeId}-${charge.seasonId}`

    return !usedCombinations.has(key) || key === currentChargeKey
  })
}

function getAvailableTimeOfUsePeriods(categoryId: number, charge: Charge){
  const usedCombinations = usedSeasonsAndTimeOfUsePeriodsPerCategory.value[categoryId]

  return TimeOfUsePeriods.filter(timeOfUsePeriod => {
    const key = `${timeOfUsePeriod.id}-${charge.seasonId}`
    const currentChargeKey = `${charge.demandTypeId}-${charge.seasonId}`

    return !usedCombinations.has(key) || key === currentChargeKey
  })
}

</script>
<template>
    <CardContainer :class="$style.category" v-for="(category) in categories" :key="category.id">
        <CardHeader uppercase>
            <span>{{ category.name }}</span>
            <span v-if="category.features.includes('hide')">
                <InputToggleGroup :options="[{ value: true, text: 'Show' }, { value: false, text: 'Hide' }]"
                                :selected="!category.hidden"
                                @update:selected="trackChange('categories', category.id, { hidden: !$event })" />
                <IconHelp class="ms-2" :tooltip="`Show or hide this\nsection on the invoice.`" />
            </span>
        </CardHeader>
        <CardContent>
            <table class="table table-no-bottom-border">
                <thead>
                    <tr>
                        <th>TOU Period</th>
                        <th>Season</th>
                        <th>Type</th>
                        <th class="text-end">Tariff (c/KWh)</th>
                        <th class="text-end">Energy Delivered (KWh)</th>
                        <th class="text-end">Total (R)</th>
                        <th>Hide</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(charge) in chargesWithChanges[category.id]" :key="charge.id">
                      <template v-if="editableChargeTypes.includes(charge.chargeType)">
                        <td>
                            <InputSelect :items="getAvailableTimeOfUsePeriods(category.id,charge)"
                                         item-value="id"
                                         item-text="name"
                                         v-model:selected="charge.demandTypeId"
                                         @update:selected="trackChange('charges',charge.id, { demandTypeId: $event, description: buildChargeDescription(charge) })"/>
                        </td>
                        <td>
                            <InputSelect :items="getAvailableSeasons(category.id,charge)"
                                        item-value="id"
                                        item-text="name"
                                        v-model:selected="charge.seasonId"
                                        @update:selected="trackChange('charges', charge.id, { seasonId: $event, description: buildChargeDescription(charge)})"/>
                        </td>
                        <td>
                          <InputSelect :items="firmChargeTypes.includes(charge.chargeType) ? mapToChargeTypeOptions(firmChargeTypes) : mapToChargeTypeOptions(nonFirmChargeTypes)"
                                        item-value="id"
                                        item-text="name"
                                        v-model:selected="charge.chargeType"
                                        @update:selected="trackChange('charges', charge.id, {chargeType: $event, description: buildChargeDescription(charge)})"/>
                        </td>
                        <td class="text-end">
                            <InputNumber :value="charge.rateInCents" @update:value="trackChange('charges', charge.id, { rateInCents: $event, description: buildChargeDescription(charge)})" style="width: 50%;float: right;"/>
                        </td>
                        <td class="text-end">
                            <InputNumber :value="charge.quantity" @update:value="trackChange('charges', charge.id, { quantity: $event, description: buildChargeDescription(charge)})" style="width: 50%;float: right;"/>
                        </td>
                        <td class="text-end">
                            {{ formatToFractionalCurrency(charge.rateInCents / 100 * charge.quantity) }}
                        </td>
                      </template>
                      <template v-else>
                        <td>{{ TimeOfUsePeriods.find(x => x.id === charge.demandTypeId)?.name ?? '' }}</td>
                        <td>{{ Seasons.find(x => x.id === charge.seasonId)?.name ?? '' }}</td>
                        <td>{{ chargeTypeDisplayNames[charge.chargeType] }}</td>
                        <td class="text-end">{{ formatToFractionalCurrency(charge.rateInCents, { minScale: 0 }) }}</td>
                        <td class="text-end">{{ formatNumber(charge.quantity) }}</td>
                        <td class="text-end">{{ formatToFractionalCurrency(charge.rateInCents / 100 * charge.quantity) }}</td>
                      </template>
                      <td style="width: 1px;">
                        <InputCheckbox :disabled="charge.id < 1" :checked="charge.hidden" @update:checked="trackChange('charges', charge.id, { hidden: $event })" />
                      </td>
                      <td>
                        <DropdownContext>
                          <DropdownItem @click="showInvoiceChargeDeletionModal(category.id, charge)">Delete</DropdownItem>
                        </DropdownContext>
                      </td>
                    </tr>
                </tbody>
            </table>

            <a v-if="capabilities.includes('add-charge')" href="javascript:void(0)" :class="$style['new-charge']" @click="addNewCharge(category.id,)">+ New Charge</a>

            <div :class="$style.summary">
                <span class="text-end">Subtotal</span>
                <span>{{ formatToMainCurrency(categoryTotalsWithChangesByCategoryId[category.id]) }}</span>
            </div>
        </CardContent>
    </CardContainer>

    <ModalVariantConfirmation v-if="confirmChargeDeletion"
                            @reject="hideInvoiceChargeDeletionModal()"
                            @confirm="handleChargeDeletion(confirmChargeDeletion)">
    <p>
      Are you sure you would like to delete the charge <b>{{ confirmChargeDeletion.description }}</b>
      for <b>{{ formatToMainCurrency(confirmChargeDeletion.rateInCents / 100) }}</b>
      from the invoice charges section of this invoice template?
    </p>
  </ModalVariantConfirmation>
</template>
<style module lang="scss">
@import "@/assets/variables";
.summary {
  background: #eee;
  font-weight: 700;
  padding: 20px 20px 20px 64.5%;
  border-bottom-left-radius: #{$card-border-radius};
  border-bottom-right-radius: #{$card-border-radius};
  display: grid;
  grid-template-columns: 167px 1fr;
  column-gap: 78px;
  margin: #{$card-spacer-y} #{-1 * $card-spacer-x} #{-1 * $card-spacer-y};
}

.category:not(:first-child) {
  margin-top: 27px;
}

.new-charge {
    text-decoration: none;
}
</style>
